/**
 * The toast stack's rules, as pure functions over `{ shown, waiting }`
 * (twin slice 1, plan W4 step 13).
 *
 * THE STACK HOLDS STILL WHILE ANY TOAST IN IT IS HELD (`ToastHold`: a
 * pointer over it, focus in it, a finger on it). The stack is anchored at
 * the bottom of the screen, newest last, so ANY change to it moves the
 * toasts that stay: a new arrival pushes every toast up a slot, and a
 * toast leaving -- dropped by the three-toast cap, or expiring below the
 * held one -- lets the toasts above it fall a slot. Either way the toast
 * that was under the finger is no longer there, and the tap lands on
 * another alert's "Show where" or body. So while anything is held:
 *
 * - nothing expires, the held toast's neighbours included;
 * - a new alert's toast WAITS (`waiting`) instead of going on screen;
 *   its chime still plays and its bell row is already there.
 *
 * When the last hold ends (or the held toast is dismissed or pressed
 * away), the waiting toasts join the stack, the cap drops the oldest, and
 * every toast on screen gets at least a fresh TTL: their clocks were
 * stopped too, and a toast read past its expiry must not vanish the
 * instant the pointer leaves.
 *
 * Only a press the person makes (×, "Show where", the toast itself)
 * changes a held stack.
 */
import type { ToastHold, ToastItem } from './constants';

export interface ToastStackState {
  /** On screen, oldest first. */
  readonly shown: readonly ToastItem[];
  /** Arrived while the stack was held; shown once it lets go. */
  readonly waiting: readonly ToastItem[];
}

export const EMPTY_TOAST_STACK: ToastStackState = { shown: [], waiting: [] };

export function isToastHeld(t: ToastItem): boolean {
  return (t.heldBy?.length ?? 0) > 0;
}

/** Whether any toast on screen is held, which freezes the whole stack. */
export function isStackHeld(state: ToastStackState): boolean {
  return state.shown.some(isToastHeld);
}

/**
 * The stack letting go: the waiting toasts join it, the cap drops the
 * oldest, and every toast on screen expires no sooner than `now + ttlMs`.
 */
function letGo(state: ToastStackState, now: number, ttlMs: number, cap: number): ToastStackState {
  const floor = now + ttlMs;
  const shown = [...state.shown, ...state.waiting]
    .slice(-cap)
    .map((t) => (t.expiresAt < floor ? { ...t, expiresAt: floor } : t));
  return { shown, waiting: [] };
}

/**
 * `after`, unless this change is the one that let a held stack go (its
 * last hold ended, or its held toast was pressed away): then `after` with
 * the waiting toasts let in and fresh TTLs (`letGo`).
 */
export function settleToastStack(
  before: ToastStackState,
  after: ToastStackState,
  now: number,
  ttlMs: number,
  cap: number,
): ToastStackState {
  return isStackHeld(before) && !isStackHeld(after) ? letGo(after, now, ttlMs, cap) : after;
}

/**
 * New toasts: onto the stack under the cap, or waiting while it is held.
 *
 * An arrival for an alert ALREADY IN THE STACK replaces that toast where it
 * is, holds kept (spa 0.5.69): a row raised in place -- a low battery gone
 * very low, a door left shut ten minutes -- is one alert with new words and
 * a new severity, and two toasts for one row would share its id. Replacing
 * it in its slot moves nothing, so it is allowed while the stack is held.
 */
export function admitToasts(
  state: ToastStackState,
  arrivals: readonly ToastItem[],
  cap: number,
): ToastStackState {
  if (arrivals.length === 0) return state;
  const pending = new Map(arrivals.map((t) => [t.id, t]));
  const swap = (t: ToastItem): ToastItem => {
    const next = pending.get(t.id);
    if (!next) return t;
    pending.delete(t.id);
    return t.heldBy ? { ...next, heldBy: t.heldBy } : next;
  };
  const shown = state.shown.map(swap);
  const waiting = state.waiting.map(swap);
  const fresh = arrivals.filter((t) => pending.has(t.id));
  if (isStackHeld(state)) return { shown, waiting: [...waiting, ...fresh] };
  return { shown: [...shown, ...fresh].slice(-cap), waiting };
}

/** The TTL sweep. Nothing expires while the stack is held. */
export function sweepToastStack(state: ToastStackState, now: number): ToastStackState {
  if (isStackHeld(state)) return state;
  const shown = state.shown.filter((t) => t.expiresAt > now);
  return shown.length === state.shown.length ? state : { ...state, shown };
}

/** Takes one toast away, on screen or waiting: a press the person made. */
export function removeToast(state: ToastStackState, id: string): ToastStackState {
  const shown = state.shown.filter((t) => t.id !== id);
  const waiting = state.waiting.filter((t) => t.id !== id);
  return shown.length === state.shown.length && waiting.length === state.waiting.length
    ? state
    : { shown, waiting };
}

/** Adds one hold to a toast on screen. */
export function holdToastIn(state: ToastStackState, id: string, hold: ToastHold): ToastStackState {
  const i = state.shown.findIndex((t) => t.id === id);
  if (i < 0 || state.shown[i]!.heldBy?.includes(hold)) return state;
  const shown = [...state.shown];
  shown[i] = { ...state.shown[i]!, heldBy: [...(state.shown[i]!.heldBy ?? []), hold] };
  return { ...state, shown };
}

/** Ends one hold on a toast on screen. */
export function releaseToastIn(
  state: ToastStackState,
  id: string,
  hold: ToastHold,
): ToastStackState {
  const i = state.shown.findIndex((t) => t.id === id);
  if (i < 0 || !state.shown[i]!.heldBy?.includes(hold)) return state;
  const shown = [...state.shown];
  shown[i] = { ...state.shown[i]!, heldBy: state.shown[i]!.heldBy!.filter((h) => h !== hold) };
  return { ...state, shown };
}
